import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Sparkles, Download, Copy, Twitter, Facebook, Share2, Loader2, ImageIcon } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FRONTEND_URL = window.location.origin;

export function PromoImageDialog({ open, onOpenChange, eventId, eventName, shareToken }) {
  const [size, setSize] = useState('square');
  const [imgSrc, setImgSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [blobCache, setBlobCache] = useState({});

  const fetchPromo = useCallback(async (chosenSize) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const cacheBust = `&_t=${Date.now()}`;
      const res = await fetch(`${API_URL}/api/events/${eventId}/promo-image?size=${chosenSize}${cacheBust}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to generate image');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setBlobCache(prev => ({ ...prev, [chosenSize]: { url, blob } }));
      setImgSrc(url);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (open) {
      // Always re-fetch fresh when dialog opens (counts may have changed)
      fetchPromo(size);
    } else {
      // Cleanup blob URLs on close to avoid leaks
      Object.values(blobCache).forEach(c => c?.url && URL.revokeObjectURL(c.url));
      setBlobCache({});
      setImgSrc(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const switchSize = (newSize) => {
    if (newSize === size) return;
    setSize(newSize);
    if (blobCache[newSize]) {
      setImgSrc(blobCache[newSize].url);
    } else {
      fetchPromo(newSize);
    }
  };

  const downloadImage = async () => {
    const cached = blobCache[size];
    if (!cached) return;
    const a = document.createElement('a');
    a.href = cached.url;
    a.download = `${(eventName || 'event').replace(/\s+/g, '_').toLowerCase()}_promo_${size}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success('Promo image downloaded');
  };

  const copyShareLink = async () => {
    if (!shareToken) {
      toast.error('Make the schedule public first to copy a share link');
      return;
    }
    const url = `${FRONTEND_URL}/share/${shareToken}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Schedule link copied to clipboard!');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const buildPublicUrl = () => shareToken ? `${FRONTEND_URL}/share/${shareToken}` : FRONTEND_URL;

  const shareNative = async () => {
    const cached = blobCache[size];
    if (!cached) return;
    const text = `${eventName} - powered by Schedule Guru`;
    const url = buildPublicUrl();
    // Try Web Share API with file (works on mobile Safari/Chrome)
    if (navigator.canShare) {
      const file = new File([cached.blob], `${eventName}_promo.png`, { type: 'image/png' });
      if (navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: eventName, text, url });
          return;
        } catch { /* user cancelled */ return; }
      }
    }
    // Fallback: download + copy URL
    downloadImage();
    await copyShareLink();
    toast.info('Image downloaded — paste your link & attach the image to your post');
  };

  const openTwitter = () => {
    const text = `🏆 ${eventName} - check out the official schedule!`;
    const url = buildPublicUrl();
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank', 'noopener,noreferrer');
  };

  const openFacebook = () => {
    const url = buildPublicUrl();
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="promo-image-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#00247D]">
            <Sparkles className="w-5 h-5 text-amber-500" />
            Promote on Social
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Generate a polished promo image for Instagram, Twitter, or Facebook. Auto-updates as more teams submit their rosters.
          </DialogDescription>
        </DialogHeader>

        {/* Size toggle */}
        <div className="flex items-center justify-center gap-2 mb-2">
          <div className="inline-flex bg-slate-100 rounded-lg p-1">
            <button
              type="button"
              onClick={() => switchSize('square')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${size === 'square' ? 'bg-white text-[#00247D] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid="promo-size-square">
              Instagram (1:1)
            </button>
            <button
              type="button"
              onClick={() => switchSize('og')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${size === 'og' ? 'bg-white text-[#00247D] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid="promo-size-og">
              Twitter / FB (1.91:1)
            </button>
          </div>
        </div>

        {/* Preview */}
        <div className="relative bg-slate-100 rounded-lg overflow-hidden flex items-center justify-center" style={{ aspectRatio: size === 'square' ? '1 / 1' : '1200 / 630', minHeight: 240 }}>
          {loading ? (
            <div className="flex flex-col items-center gap-2 text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin" />
              <span className="text-sm">Generating fresh image…</span>
            </div>
          ) : imgSrc ? (
            <img src={imgSrc} alt="Tournament promo" className="w-full h-full object-contain" data-testid="promo-image-preview" />
          ) : (
            <div className="flex flex-col items-center gap-2 text-slate-400">
              <ImageIcon className="w-8 h-8" />
              <span className="text-sm">No preview yet</span>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
          <Button onClick={downloadImage} disabled={loading || !imgSrc} className="bg-[#00247D] hover:bg-[#001A5C] text-white" data-testid="promo-download-btn">
            <Download className="w-4 h-4 mr-1.5" /> Download
          </Button>
          <Button onClick={copyShareLink} variant="outline" data-testid="promo-copy-link-btn" disabled={!shareToken}>
            <Copy className="w-4 h-4 mr-1.5" /> Copy Link
          </Button>
          <Button onClick={openTwitter} variant="outline" className="text-sky-600 border-sky-200 hover:bg-sky-50" data-testid="promo-twitter-btn" disabled={!shareToken}>
            <Twitter className="w-4 h-4 mr-1.5" /> Tweet
          </Button>
          <Button onClick={openFacebook} variant="outline" className="text-blue-700 border-blue-200 hover:bg-blue-50" data-testid="promo-facebook-btn" disabled={!shareToken}>
            <Facebook className="w-4 h-4 mr-1.5" /> Facebook
          </Button>
        </div>

        {typeof navigator !== 'undefined' && navigator.canShare && (
          <Button onClick={shareNative} variant="ghost" className="mt-1" data-testid="promo-native-share-btn" disabled={!imgSrc}>
            <Share2 className="w-4 h-4 mr-1.5" /> Share via device…
          </Button>
        )}

        {!shareToken && (
          <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md p-2 mt-2">
            Tip: enable the public share link on this schedule to also share it directly to social platforms.
          </p>
        )}

        <p className="text-[11px] text-slate-400 mt-1 text-center">
          The image refreshes automatically as more teams submit their rosters.
        </p>
      </DialogContent>
    </Dialog>
  );
}
